const VALID_VAT_RATES = new Set([0, 5, 18, 27]);

/**
 * Default VAT rate applied to a food/extra line that has no VAT configured
 * anywhere on the item / size / product / food chain.
 *
 * Per business rule: "If food there is no vat use 0 as default".
 * This is intentionally 0 (and NOT 27) so that operators do not get a
 * surprise 27% line on the receipt for items the back-office never tagged.
 */
export const DEFAULT_FOOD_VAT_RATE = 0;

export const normalizeOrderType = (type = '', fallback = '') => {
  const raw = String(type || '').trim();
  const lowerCasedType = raw.toLowerCase();

  if (!lowerCasedType) {
    return fallback;
  }

  if (raw === 'dineIn' || raw === 'takeAway' || raw === 'online') {
    return raw;
  }

  if (
    lowerCasedType === 'dinein'
    || lowerCasedType === 'dine-in'
    || lowerCasedType === 'dine in'
    || lowerCasedType === 'table'
  ) {
    return 'dineIn';
  }

  if (
    lowerCasedType === 'takeaway'
    || lowerCasedType === 'take away'
    || lowerCasedType === 'take-away'
  ) {
    return 'takeAway';
  }

  if (
    lowerCasedType === 'online'
    || lowerCasedType === 'delivery'
    || lowerCasedType === 'home delivery'
    || lowerCasedType === 'home-delivery'
    || lowerCasedType === 'home_delivery'
  ) {
    return 'online';
  }

  return fallback || raw;
};

export const resolveVatOrderType = (order = {}) => {
  const normalizedOrderType = normalizeOrderType(
    order?.orderType,
    order?.tableId || order?.tableName ? 'dineIn' : ''
  );

  if (normalizedOrderType) {
    return normalizedOrderType;
  }

  const hasAddressInfo = Boolean(
    order?.addressInfo
    && typeof order.addressInfo === 'object'
    && Object.keys(order.addressInfo).length > 0
  );

  if (hasAddressInfo) {
    return 'online';
  }

  return 'takeAway';
};

export const normalizeVatRate = (vatValue, fallback = 27) => {
  const numericVat = Number(vatValue);

  if (VALID_VAT_RATES.has(numericVat)) {
    return numericVat;
  }

  return VALID_VAT_RATES.has(Number(fallback)) ? Number(fallback) : 27;
};

export const resolveVatRate = (vatConfig = {}, orderOrType = '', fallback = 27) => {
  const orderType = typeof orderOrType === 'string'
    ? normalizeOrderType(orderOrType, 'takeAway')
    : resolveVatOrderType(orderOrType);

  const candidateRates = [
    vatConfig?.[orderType],
    orderType === 'online' ? vatConfig?.delivery : undefined,
    vatConfig?.dineIn,
    vatConfig?.takeAway,
    vatConfig?.online,
    vatConfig?.delivery,
    vatConfig?.default,
    vatConfig,
  ];

  const resolvedRate = candidateRates.find((rate) => Number.isFinite(Number(rate)));
  return normalizeVatRate(resolvedRate, fallback);
};

const resolveLineSize = (lineItem = {}) => {
  const nestedSizes = Array.isArray(lineItem?.item?.sizes) ? lineItem.item.sizes : [];
  const directSizes = Array.isArray(lineItem?.sizes) ? lineItem.sizes : [];
  const sizes = nestedSizes.length > 0 ? nestedSizes : directSizes;

  if (!sizes.length) {
    return null;
  }

  const explicitSizeId = lineItem?.size || lineItem?.sizeId || lineItem?.item?.size || '';
  const explicitIndex = typeof lineItem?.sizeIndex === 'number'
    ? lineItem.sizeIndex
    : (typeof lineItem?.sizeIndex === 'string' && lineItem.sizeIndex !== '' && !Number.isNaN(Number(lineItem.sizeIndex))
      ? Number(lineItem.sizeIndex)
      : null);

  if (explicitSizeId) {
    const matchingSize = sizes.find((size) => {
      return size?._id === explicitSizeId || size?.id === explicitSizeId;
    });

    if (matchingSize) {
      return matchingSize;
    }
  }

  if (explicitIndex !== null && sizes[explicitIndex]) {
    return sizes[explicitIndex];
  }

  return sizes[0] || null;
};

export const resolveLineVatRate = (lineItem = {}, orderOrType = '', fallbackVat = DEFAULT_FOOD_VAT_RATE) => {
  const resolvedSize = resolveLineSize(lineItem);
  // Both the canonical `vat` and the legacy plural `vats` are read on every
  // shape — the food catalog stores the per-orderType rates under `vat` today
  // but older/synced foods (and `slimFoodForCache`) may still carry `vats`.
  // `vat` takes priority over `vats` on the same object.
  const vatCandidates = [
    resolvedSize?.vat,
    resolvedSize?.vats,
    lineItem?.item?.vat,
    lineItem?.item?.vats,
    lineItem?.vat,
    lineItem?.vats,
    lineItem?.product?.vat,
    lineItem?.product?.vats,
    lineItem?.food?.vat,
    lineItem?.food?.vats,
    lineItem?.item?.item?.vat,
    lineItem?.item?.item?.vats,
    lineItem?.item?.product?.vat,
    lineItem?.item?.product?.vats,
    lineItem?.item?.food?.vat,
    lineItem?.item?.food?.vats,
  ];

  const hasAnyConfiguredVat = vatCandidates.some(
    (candidate) => candidate !== null && typeof candidate !== 'undefined' && candidate !== ''
  );

  // If no VAT configuration exists anywhere on the item, default to 0%
  // (food without VAT). Previously this returned 27% via the "B" code,
  // which produced incorrect tax on receipts for un-tagged items.
  if (!hasAnyConfiguredVat) {
    return DEFAULT_FOOD_VAT_RATE;
  }

  const vatConfig = vatCandidates.find((candidate) => candidate !== null && typeof candidate !== 'undefined' && candidate !== '');
  return resolveVatRate(vatConfig, orderOrType, fallbackVat);
};

